/**
 * Meraki workflows store — list of workflows the user can run.
 *
 * Why a store instead of useState in the page:
 *   - The detail page (/meraki/provisioning/run/:slug) needs the same data
 *     and shouldn't re-fetch.
 *   - The workflow list rarely changes; caching here lets us return instantly
 *     on revisit and refresh in the background.
 *
 * Pattern: each store action does a try/catch. On error we keep the previous
 * data and surface `error` so the UI can show a non-blocking warning instead
 * of a full-page error state.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { WorkflowListSchema, type Workflow } from "@/api/schemas/meraki";
import { MOCK_WORKFLOWS } from "@/api/fixtures/meraki-workflows";

interface State {
  data: Workflow[];
  /**
   * "idle"   — never fetched
   * "loading" — fetch in flight (data may be empty or stale)
   * "ready"  — last fetch succeeded
   * "error"  — last fetch failed (data falls back to mocks)
   */
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
}

export const useMerakiWorkflows = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/workflows/");
      // Validate the response shape — the source of truth is the Zod schema,
      // not the TypeScript type. If the API drifts, this throws loudly.
      const parsed = WorkflowListSchema.parse(raw);
      set({ data: parsed, status: "ready" });
    } catch (err) {
      // Fall back to mocks so the UI never sits empty during an outage. We
      // still record the error so a small banner can warn the user that
      // they're seeing fixture data.
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_WORKFLOWS,
        status: "error",
        error: `${message} — showing local fixture data`,
      });
    }
  },
}));
